import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import type { StayFullCreateResult, StayOccupancy, StaysListingHead, StaysPolicy } from '../../../../interfaces/stays';
import { createStay, getListingOccupancy, ReservationConflictError } from '../../../../api/stays-reservations-client';
import { rotateLink, StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ClockNote } from '../../../clock-note/clock-note';
import { BackBar, BackButton } from '../../../layout/back-button';
import { HeaderRow, HeaderSub, HeaderTitle } from '../../../layout/page-header.styled';
import { DateRangePicker, DateRangeValue, OccupiedRange } from '../../../shared/date-range-picker/date-range-picker';
import { FieldError } from '../../../form-kit';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { Shake, StatusLabel } from '../../../../motion';
import toast from '../../../../utils/toast';
import { formatMoney } from '../../../../utils/stays-money';
import { formatDayKey, todayIn } from '../../../../function-utils/building-time';
import { palette, radius, shadow, space, spacing, typography, zIndex } from '../../../../style-utils/theme';
import { up } from '../../../../style-utils/media-queries';
import { buildingClock, formatStayDay, nightsText } from '../stays-reservations-model';
import { StayLinkPanel, IssuedLink } from '../stay-link-panel';
import { stayHref } from '../listing/stays-listing-routes';
import {
  amountError,
  buildCreateInput,
  applyPatch,
  conflictSentence,
  contextOf,
  effectivePayout,
  emptyForm,
  FieldErrors,
  firstErrorKey,
  focusField,
  FormKey,
  MONEY_KEYS,
  moneyErrors,
  nightsOf,
  StayForm,
  validateForm,
} from './reservation-form-model';
import { FieldKit, GuestFields, NotesFields } from './reservation-fields';
import { MoneyFields } from './money-fields';
import { useAuth } from '../../../../context/auth-provider';
import { isCohost } from '../../../../auth/roles';
import { CleaningFields } from './cleaning-fields';
import { AlertBanner, FormSection, NeutralNotice, SectionTitle } from './reservation-form.styled';

/**
 * NEW STAY (ai-plans/stays plan S4, work package S4.9) — the host records a
 * booking taken elsewhere, at /dashboard/stays/<listing id>/new.
 *
 * The listing shell renders this page inside the dashboard's single <main>
 * (its one h1 is the dashboard heading and the listing name is an h2), so the
 * page title is an h2 drawn with the ScreenHeader parts rather than
 * ScreenHeader itself, whose title is an h1.
 *
 * Dates come from the Stays range picker, fed with the listing's booked and
 * closed nights (GET …/occupancy) and the BUILDING's today from the same
 * answer. Save stays disabled — with the reason on screen — until there is a
 * range that clashes with nothing. The stay is saved with no check-in link
 * (issue_link: false); "Make the check-in link" makes one afterwards and shows
 * it once, in plan S3's panel.
 *
 * One request per save: a ref guards a double press before React has
 * re-rendered the disabled button.
 */

export interface NewReservationPageProps {
  listing: StaysListingHead;
  policy?: StaysPolicy;
}

type OccState = { state: 'loading' | 'ready' | 'error'; data: StayOccupancy | null };

const DAY: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

const Head = styled.div`
  min-width: 0;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.md};
  align-items: start;
  min-width: 0;

  ${up('lg')} {
    grid-template-columns: minmax(0, 1fr) ${space(36)};
  }
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

const Aside = styled.aside`
  display: none;

  ${up('lg')} {
    position: sticky;
    top: ${spacing.md};
    display: block;
    min-width: 0;
    padding: ${spacing.md};
    background: ${palette.surface};
    border: 1px solid ${palette.line};
    border-radius: ${radius.md};
    box-shadow: ${shadow.sm};
  }
`;

const AsideTitle = styled.h3`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.xs};
  margin: 0;
`;

const FactTerm = styled.dt`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
`;

const FactValue = styled.dd`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

/* Pinned to the bottom of the scrolling page. The button sits on the LEFT:
   the IQ launcher is fixed in the bottom-right corner. */
const Footer = styled.div`
  position: sticky;
  bottom: 0;
  z-index: ${zIndex.sticky};
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm} 0;
  padding-bottom: calc(${spacing.sm} + var(--safe-bottom));
  background: ${palette.porcelain};
  border-top: 1px solid ${palette.line};
`;

const SavedCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};

  &:focus {
    outline: none;
  }
`;

const SavedTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const SavedLine = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

const SavedActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  margin-top: ${spacing.sm};
`;

const PickerSlot = styled.div`
  min-height: ${space(10)};
  min-width: 0;
`;

/** The sentence a picker conflict disables Save with. */
const conflictReason = (range: OccupiedRange): string =>
  range.kind === 'stay' ? `These dates overlap ${range.label}'s stay` : `These dates include closed nights (${range.label})`;

export const NewReservationPage: React.FC<NewReservationPageProps> = ({ listing, policy }) => {
  const uid = useId();
  const fid = useCallback((key: string) => `${uid}-${key}`, [uid]);
  const navigate = useNavigate();
  // IS5: a co-host sees and sends no amounts.
  const hideMoney = isCohost(useAuth().user?.role);
  const ctx = useMemo(() => contextOf(listing, policy, hideMoney), [listing, policy, hideMoney]);

  const [form, setForm] = useState<StayForm>(() => emptyForm(ctx));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [clash, setClash] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const [saved, setSaved] = useState<StayFullCreateResult | null>(null);
  const savedRef = useRef<HTMLElement>(null);

  const [occ, setOcc] = useState<OccState>({ state: 'loading', data: null });
  const readSeq = useRef(0);
  const [conflicts, setConflicts] = useState<OccupiedRange[]>([]);

  const [issued, setIssued] = useState<IssuedLink | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const linkBusy = useRef(false);
  const [linkProblem, setLinkProblem] = useState('');

  const loadOccupancy = useCallback(() => {
    const seq = ++readSeq.current;
    setOcc(current => ({ state: current.data ? current.state : 'loading', data: current.data }));
    getListingOccupancy(listing.id)
      .then(data => {
        if (seq === readSeq.current) setOcc({ state: 'ready', data });
      })
      .catch(() => {
        if (seq === readSeq.current) setOcc(current => ({ state: 'error', data: current.data }));
      });
  }, [listing.id]);

  useEffect(() => {
    loadOccupancy();
  }, [loadOccupancy]);

  const zone = occ.data?.building_zone || listing.timezone;
  const today = occ.data?.building_today || todayIn(zone) || '';
  const occupied = useMemo<OccupiedRange[]>(
    () => (occ.data ? occ.data.ranges.map(r => ({ start: r.start, end: r.end, kind: r.kind, label: r.label })) : []),
    [occ.data]
  );
  const range: DateRangeValue = { start: form.check_in || null, end: form.check_out || null };
  const nights = nightsOf(form.check_in, form.check_out);
  const inPast = !!form.check_out && !!today && form.check_out <= today;

  const saveReason = !form.check_in || !form.check_out
    ? 'Choose check-in and check-out'
    : conflicts.length > 0
      ? conflictReason(conflicts[0])
      : null;

  const update = (patch: Partial<StayForm>) => {
    setForm(current => applyPatch(current, patch, ctx));
    setErrors(current => {
      const next = { ...current };
      for (const key of Object.keys(patch)) delete next[key];
      if (patch.check_in !== undefined || patch.check_out !== undefined) {
        delete next.check_in;
        delete next.check_out;
      }
      if (patch.cleaning_kind !== undefined) {
        delete next.cleaning;
        delete next.cleaning_fee;
      }

      return next;
    });
  };

  const blur = (key: FormKey) => {
    setErrors(current => {
      const next = { ...current };
      if (key === 'cleaning_fee') {
        const problem = form.cleaning_fee.trim() ? amountError(form.cleaning_fee, ctx.currency) : null;
        if (problem) next.cleaning_fee = problem;
        else delete next.cleaning_fee;

        return next;
      }
      const all = moneyErrors(form, ctx);
      if (all[key]) next[key] = all[key];
      else delete next[key];
      for (const k of MONEY_KEYS) if (k !== key && next[k] && !all[k]) delete next[k];

      return next;
    });
  };

  const focusKey = (key: string | null) => {
    if (!key) return;
    focusField(key === 'check_in' || key === 'check_out' ? fid('dates') : fid(key));
  };

  const fail = (error: unknown) => {
    setAttempt(a => a + 1);
    if (error instanceof StaysApiError && error.status === 400 && Object.keys(error.fields).length) {
      setErrors(error.fields);
      const known = Object.keys(error.fields).some(k => k !== 'issue_link' && k !== 'external_uid');
      if (!known) setRefusal(error.message);
      focusKey(firstErrorKey(error.fields, error.field));

      return;
    }
    if (error instanceof ReservationConflictError) {
      const sentence = conflictSentence(error.code, error.conflict);
      if (sentence) {
        setClash(sentence);
        loadOccupancy();
        focusField(fid('clash'));

        return;
      }
    }
    setRefusal(refusalText(error as StaysApiError, 'Could not save the stay. Try again.'));
  };

  const submit = async () => {
    if (busy.current || saveReason) return;
    setRefusal(null);
    setClash(null);
    const errs = validateForm(form, ctx);
    if (Object.keys(errs).length) {
      setErrors(errs);
      setAttempt(a => a + 1);
      focusKey(firstErrorKey(errs));

      return;
    }
    busy.current = true;
    setSaving(true);
    const input = buildCreateInput(form, ctx);
    try {
      const result = await createStay(listing.id, input);
      setErrors({});
      setSaved(result);
      toast.success(`Stay saved for ${result.reservation.lead.first_name || input.lead_first_name}.`);
      window.setTimeout(() => savedRef.current?.focus(), 0);
    } catch (error) {
      fail(error);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const reset = () => {
    setForm(emptyForm(ctx));
    setErrors({});
    setRefusal(null);
    setClash(null);
    setAttempt(0);
    setSaved(null);
    setIssued(null);
    setLinkOpen(false);
    setLinkProblem('');
    setConflicts([]);
    loadOccupancy();
  };

  const makeLink = async () => {
    if (!saved || linkBusy.current) return;
    linkBusy.current = true;
    setLinking(true);
    setLinkProblem('');
    try {
      const made = await rotateLink(saved.reservation.id);
      setIssued({ link: made.link, email: made.email });
      setLinkOpen(true);
    } catch (error) {
      setLinkProblem(refusalText(error as StaysApiError, 'Could not make the check-in link. Try again.'));
    } finally {
      linkBusy.current = false;
      setLinking(false);
    }
  };

  const kit: FieldKit = { form, errors, disabled: saving, update, blur, id: fid };
  const payout = effectivePayout(form, ctx);
  const datesError = errors.check_in || errors.check_out;

  const header = (
    <Head>
      <BackBar>
        <BackButton to={`/dashboard/stays/${listing.id}/stays`} label="Back to stays" />
      </BackBar>
      <HeaderRow>
        <div>
          <HeaderTitle as="h2">New stay</HeaderTitle>
          <HeaderSub>{`Record a booking for ${listing.name}.`}</HeaderSub>
        </div>
      </HeaderRow>
    </Head>
  );

  if (saved) {
    const r = saved.reservation;
    const name = [r.lead.first_name, r.lead.last_name].filter(Boolean).join(' ');
    const n = nightsOf(r.check_in, r.check_out) || r.nights;

    return (
      <Page>
        {header}
        <SavedCard ref={savedRef} tabIndex={-1} aria-labelledby={fid('saved-title')} data-testid="stay-saved">
          <SavedTitle id={fid('saved-title')}>Stay saved</SavedTitle>
          <SavedLine>{name}</SavedLine>
          <SavedLine>{`${formatStayDay(r.check_in)} → ${formatStayDay(r.check_out)}`}</SavedLine>
          <SavedLine>{nightsText(n)}</SavedLine>
          {saved.notices.includes('past') ? (
            <NeutralNotice role="status">Recorded as a past stay, for your records.</NeutralNotice>
          ) : null}
          {linkProblem ? <AlertBanner role="alert">{linkProblem}</AlertBanner> : null}
          <SavedActions>
            <AppButton onClick={() => navigate(stayHref(listing.id, r.id))}>Open stay</AppButton>
            <AppButton variant="secondary" onClick={reset}>
              Add another stay
            </AppButton>
            {issued ? (
              <StatusPill tone="success" dot>
                Check-in link made
              </StatusPill>
            ) : (
              <AppButton variant="secondary" disabled={linking} onClick={() => void makeLink()}>
                <StatusLabel
                  state={linking ? 'busy' : 'idle'}
                  labels={{ idle: 'Make the check-in link', busy: 'Making the link…' }}
                />
              </AppButton>
            )}
          </SavedActions>
        </SavedCard>
        <StayLinkPanel open={linkOpen} reservation={r} issued={issued} onClose={() => setLinkOpen(false)} />
      </Page>
    );
  }

  return (
    <Page>
      {header}
      <Shake active={attempt > 0 && (Object.keys(errors).length > 0 || !!clash || !!refusal)} attempt={attempt}>
        <form
          noValidate
          aria-label="New stay"
          onSubmit={event => {
            event.preventDefault();
            void submit();
          }}
        >
          <Layout>
            <Sections>
              {refusal ? <AlertBanner role="alert">{refusal}</AlertBanner> : null}

              <GuestFields kit={kit} maxGuests={listing.max_guests} headingId={fid('h-guest')} />

              <FormSection aria-labelledby={fid('h-dates')}>
                <SectionTitle id={fid('h-dates')}>Dates</SectionTitle>
                {clash ? (
                  <AlertBanner role="alert" id={fid('clash')} tabIndex={-1} data-testid="stay-clash">
                    {clash}
                  </AlertBanner>
                ) : null}
                {occ.state === 'error' ? (
                  <LoadError message="Couldn't load the booked dates." onRetry={loadOccupancy} />
                ) : null}
                <PickerSlot>
                  {occ.state === 'loading' && !occ.data ? (
                    <FormSkeleton fields={3} label="dates" />
                  ) : (
                    <DateRangePicker
                      id={fid('dates')}
                      label="Stay dates"
                      value={range}
                      today={today}
                      occupied={occupied}
                      disabled={saving}
                      onConflictChange={setConflicts}
                      onChange={next => {
                        setClash(null);
                        update({ check_in: next.start || '', check_out: next.end || '' });
                      }}
                      zoneNote={
                        <ClockNote
                          inline
                          clock={buildingClock(zone)}
                          suffix={`· Dates are ${listing.building_name} dates`}
                        />
                      }
                    />
                  )}
                </PickerSlot>
                <FieldError id={`${fid('dates')}-error`} message={datesError} />
                {inPast ? (
                  <NeutralNotice role="status" data-testid="stay-past">
                    This stay is in the past
                  </NeutralNotice>
                ) : null}
              </FormSection>

              {hideMoney ? null : <MoneyFields kit={kit} ctx={ctx} headingId={fid('h-money')} summary="percent" />}
              <CleaningFields kit={kit} ctx={ctx} headingId={fid('h-cleaning')} />
              <NotesFields kit={kit} headingId={fid('h-notes')} />

              <Footer>
                <ActionWithReason reason={saving ? null : saveReason}>
                  {describedBy => (
                    <AppButton type="submit" disabled={saving || !!saveReason} aria-describedby={describedBy}>
                      <StatusLabel state={saving ? 'busy' : 'idle'} labels={{ idle: 'Save stay', busy: 'Saving…' }} />
                    </AppButton>
                  )}
                </ActionWithReason>
              </Footer>
            </Sections>

            <Aside aria-label="Stay summary">
              <AsideTitle>Summary</AsideTitle>
              <Facts>
                <FactTerm>Check-in</FactTerm>
                <FactValue>
                  {form.check_in ? `${formatDayKey(form.check_in, DAY)}, from ${listing.check_in_time}` : 'Not chosen yet'}
                </FactValue>
                <FactTerm>Check-out</FactTerm>
                <FactValue>
                  {form.check_out ? `${formatDayKey(form.check_out, DAY)}, by ${listing.check_out_time}` : 'Not chosen yet'}
                </FactValue>
                <FactTerm>Nights</FactTerm>
                <FactValue>{nights ? nightsText(nights) : '—'}</FactValue>
                {hideMoney ? null : (
                  <>
                    <FactTerm>Payout</FactTerm>
                    <FactValue>{payout === null ? '—' : formatMoney(payout, ctx.currency)}</FactValue>
                  </>
                )}
              </Facts>
            </Aside>
          </Layout>
        </form>
      </Shake>
    </Page>
  );
};
